import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { fetchHolterEventsDailyCount, isAppliedFilterEvent } from '@/entities/event';
import {
  canAvgRhythmEvents,
  canShowCountBeatEvents,
  canShowDurationEvents,
  KEY_CANCEL,
  KEY_RECOIL,
  TYPE_RHYTHM_EVENT_ENUM,
} from '@/shared/config';
import { useUpdateEffect } from '@/shared/lib/customHooks';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { logError } from '@/shared/lib/apiHelpers';

import {
  selectActiveTab,
  selectDailyCount,
  selectEventFilter,
  selectIsEmptyData,
  selectIsLoadingHrChart,
  selectIsLoadingHrChartOptions,
  selectIsShowAfibEvent,
  selectIsShowOtherEvent,
  selectJumpDate,
  selectHolterEvents,
  selectSelectedDateValue,
  selectSelectedStrip,
  useReportStore,
} from '@/entities/report';
import type { AfibHrChartOptionsProps } from './afibHrChartOptions';

/**
 * Backs the date and rhythm-event controls sitting above the AFib HR chart. Fetches the per-day event counts for the active rhythm type under the current filter, builds the MM/DD dropdown labels carrying a count or a no-data marker, and moves the selected day on a dropdown pick, an arrow click, a jump request or the selected strip's own date, clearing jump state and the empty-data flag as it goes.
 *
 * @param props - the study, profile, report window and timezone offset the counts are fetched for.
 * @returns An object carrying the loading flags, date index, daily counts, dropdown options and the date and toggle handlers.
 */
const useAfibHrChartOptions = ({
  timezoneOffset,
  studyId,
  profileId,
  startReport,
  stopReport,
}: AfibHrChartOptionsProps) => {
  const keyRecoil = KEY_RECOIL.BEAT_HR;
  const isLoadingHrChartOptions = useReportStore(selectIsLoadingHrChartOptions(keyRecoil));
  const setIsLoadingHrChartOptionsAction = useReportStore(
    (s) => s.actions.setIsLoadingHrChartOptions,
  );
  /**
   * Toggles the loading indicator for the chart's date controls. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setIsLoadingHrChartOptions = (v: Parameters<typeof setIsLoadingHrChartOptionsAction>[1]) =>
    setIsLoadingHrChartOptionsAction(keyRecoil, v);
  const activeButton = useReportStore(selectActiveTab(keyRecoil));
  const dailyCount = useReportStore(selectDailyCount(keyRecoil));
  const setDailyCountAction = useReportStore((s) => s.actions.setDailyCount);
  /**
   * Stores the per-day event counts for the beats and HR tab. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setDailyCount = (v: Parameters<typeof setDailyCountAction>[1]) =>
    setDailyCountAction(keyRecoil, v);
  /**
   * Reads the report day currently selected for the HR chart. Selects the value from the report store snapshot rather than subscribing, so the change handler can compare a picked date against the live one.
   *
   * @returns The ISO timestamp of the selected report day.
   */
  const getSelectedDateValue = () => selectSelectedDateValue(keyRecoil)(useReportStore.getState());
  const setSelectedDateValueAction = useReportStore((s) => s.actions.setSelectedDateValue);
  /**
   * Selects the report day the HR chart renders. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setSelectedDateValue = (v: Parameters<typeof setSelectedDateValueAction>[1]) =>
    setSelectedDateValueAction(keyRecoil, v);
  const isShowAfibEvent = useReportStore(selectIsShowAfibEvent(keyRecoil));
  const setIsShowAfibEventAction = useReportStore((s) => s.actions.setIsShowAfibEvent);
  /**
   * Controls whether AFib event overlays are drawn on the chart. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setIsShowAfibEvent = (v: Parameters<typeof setIsShowAfibEventAction>[1]) =>
    setIsShowAfibEventAction(keyRecoil, v);
  const isShowOtherEvent = useReportStore(selectIsShowOtherEvent(keyRecoil));
  const setIsShowOtherEventAction = useReportStore((s) => s.actions.setIsShowOtherEvent);
  /**
   * Controls whether non-AFib event overlays are drawn on the chart. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setIsShowOtherEvent = (v: Parameters<typeof setIsShowOtherEventAction>[1]) =>
    setIsShowOtherEventAction(keyRecoil, v);
  const isLoadingHrChart = useReportStore(selectIsLoadingHrChart(keyRecoil));
  // selectedStripState (Recoil/index.ts, Task 7) is typed IEvent | IBeat | IEcgBookmark; this file
  // reads event-only fields (start, startIndex) that IBeat/IEcgBookmark lack - pre-existing gap.
  const selectedStrip = useReportStore(selectSelectedStrip(keyRecoil)) as any;
  const setSelectedStripAction = useReportStore((s) => s.actions.setSelectedStrip);
  /**
   * Selects the strip highlighted across the beats and HR tab. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setSelectedStrip = (v: Parameters<typeof setSelectedStripAction>[1]) =>
    setSelectedStripAction(keyRecoil, v);
  const jumpDate = useReportStore(selectJumpDate(keyRecoil));
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Sets the report day a pending jump request should land on. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const setJumpTimeAction = useReportStore((s) => s.actions.setJumpTime);
  /**
   * Sets the time of day a pending jump request should land on. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setJumpTime = (v: Parameters<typeof setJumpTimeAction>[1]) =>
    setJumpTimeAction(keyRecoil, v);
  const setJumpPortionAction = useReportStore((s) => s.actions.setJumpPortion);
  /**
   * Sets the within-day portion a pending jump request should land on. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setJumpPortion = (v: Parameters<typeof setJumpPortionAction>[1]) =>
    setJumpPortionAction(keyRecoil, v);
  const isEmptyData = useReportStore(selectIsEmptyData(keyRecoil));
  const setIsEmptyDataAction = useReportStore((s) => s.actions.setIsEmptyData);
  /**
   * Flags whether the selected report day has any chartable data. Forwards the value to the store action already bound to this tab's key.
   *
   * @param v - the new value, or an updater receiving the previous one.
   * @returns The result of the bound store action.
   */
  const setIsEmptyData = (v: Parameters<typeof setIsEmptyDataAction>[1]) =>
    setIsEmptyDataAction(keyRecoil, v);
  const dateList = useReportStore((s) => s.dateList);
  const setDateList = useReportStore((s) => s.actions.setDateList);
  const holterEvents = useReportStore(selectHolterEvents(keyRecoil));
  const eventFilter = useReportStore(selectEventFilter(keyRecoil));
  /**
   * Reads the rhythm-event tab currently selected in the beats and HR tab. Pulls the active tab off the report store snapshot rather than subscribing, so the daily-count fetch always uses the latest type.
   *
   * @returns The active rhythm event type for this tab.
   */
  const getActiveButton = () => selectActiveTab(keyRecoil)(useReportStore.getState());

  const [dateIndex, setDateIndex] = useState(0);

  const isAfibEvent = activeButton === TYPE_RHYTHM_EVENT_ENUM.AFIB;

  const dropdownOptions = useMemo(
    () =>
      _.map(
        dailyCount,
        (x) =>
          `${convertToMomentJs(x.date, timezoneOffset).format('MM/DD')} ${x.count === -1 ? '(No data)' : `(${x.count})`}`,
      ),
    [dailyCount],
  );

  /**
   * Accepts a rhythm-event overlay toggle from the chart controls but currently changes nothing. The artifact, AFib and other-event branches are all commented out, so the overlay visibility flags stay exactly as the mount effect left them.
   *
   * @param name - the overlay the caller asked to toggle.
   * @returns Nothing.
   */
  const toggleDisplayRhythmEvent = (name: string) => {
    // switch (name) {
    //   case ENUM_EVENT_ACTION.ARTIFACT_EVENT: {
    //     setIsShowArtifactEvent(!isShowArtifactEvent);
    //     break;
    //   }
    //   case ENUM_EVENT_ACTION.AFIB_EVENT: {
    //     setIsShowAfibEvent(!isShowAfibEvent);
    //     break;
    //   }
    //   case ENUM_EVENT_ACTION.OTHER_EVENT: {
    //     setIsShowOtherEvent(!isShowOtherEvent);
    //     break;
    //   }
    //   default: {
    //     break;
    //   }
    // }
  };

  /**
   * Loads the per-day event counts that populate the chart's date dropdown. Raises the options loading flag, folds the reviewed, captured and sort filters into the request when a filter applies and the active type supports beat counts, durations or averages, fetches the counts across the whole report window under the holter-AI cancel key, stores them, and logs any failure before clearing the flag.
   *
   * @returns A promise resolving once the daily counts are stored and the loading flag is cleared.
   */
  const handleFetchHolterEventsDailyCount = async () => {
    setIsLoadingHrChartOptions(true);
    try {
      // activeTabState (Recoil/index.ts, Task 7) is typed as a broad union including TBeatHrActiveTab,
      // which canShowCountBeatEvents/canShowDurationEvents/canAvgRhythmEvents don't include - pre-existing.
      const type: any = getActiveButton();
      const additionalFilter = {};
      if (isAppliedFilterEvent(eventFilter, type)) {
        _.assign(additionalFilter, {
          ...(eventFilter.isHideReviewed && { isReviewed: false }),
          ...(eventFilter.isCapture && { isCaptured: true }),
          ...(eventFilter.sortOrder &&
            (canShowCountBeatEvents.includes(type) ||
              canShowDurationEvents.includes(type) ||
              canAvgRhythmEvents.includes(type)) && {
              sortBy: eventFilter.sortBy || 'countBeats',
              sortOrder: eventFilter.sortOrder,
            }),
        });
      }
      const holterEventsDailyCountFilter = {
        studyId,
        profileId,
        types: [type],
        startSearchDate: startReport,
        stopSearchDate: stopReport,
        ...additionalFilter,
      };
      const dailyCount = await fetchHolterEventsDailyCount(
        holterEventsDailyCountFilter,
        KEY_CANCEL.API_HOLTER_AI,
      );
      setDailyCount(dailyCount);
    } catch (error) {
      logError('Failed to fetch holter events daily count: ', error);
    }
    setIsLoadingHrChartOptions(false);
  };

  /**
   * Applies a report day picked from the date dropdown. Resolves the label back to its daily-count entry and, only when the resulting date differs from the one already selected, clears the jump date, time and portion, moves the date index, selects the new day and marks it empty when its count is minus one.
   *
   * @param name - the control name reported by the dropdown, unused here.
   * @param value - the dropdown label the user picked.
   * @returns Nothing.
   */
  const onChangeDate = (name: string, value: any) => {
    const index = _.findIndex(dropdownOptions, (x) => x === value);
    if (index > -1) {
      const selectedDateMoment = convertToMomentJs(dailyCount[index].date, timezoneOffset);
      const changedSelectedDateValue = selectedDateMoment.toISOString();
      if (changedSelectedDateValue !== getSelectedDateValue()) {
        setJumpDate(undefined);
        setJumpTime(undefined);
        setJumpPortion(0);
        setDateIndex(index);
        setSelectedDateValue(changedSelectedDateValue);
        setIsEmptyData(dailyCount[index].count === -1);
      }
    }
  };

  /**
   * Steps the chart one report day forward or back. Clears the pending jump date, time and portion, moves the date index, selects that day from the daily counts, marks the day empty when its count is minus one, and drops the selected strip while the AFib tab is active.
   *
   * @param isRight - true to move to the next day, false for the previous one.
   * @returns Nothing.
   */
  const handleClickArrow = (isRight: boolean) => {
    const newDateIndex = isRight ? dateIndex + 1 : dateIndex - 1;
    setJumpDate(undefined);
    setJumpTime(undefined);
    setJumpPortion(0);
    setDateIndex(newDateIndex);
    setSelectedDateValue(dailyCount[newDateIndex].date);
    setIsEmptyData(dailyCount[newDateIndex].count === -1);
    if (activeButton === TYPE_RHYTHM_EVENT_ENUM.AFIB) {
      setSelectedStrip(null);
    }
  };

  useEffect(() => {
    if (isAfibEvent) {
      handleFetchHolterEventsDailyCount();
      setIsShowAfibEvent(true);
    } else {
      setIsShowOtherEvent(true);
    }
  }, [activeButton, eventFilter, isAfibEvent]);

  useUpdateEffect(() => {
    if (!isLoadingHrChartOptions) {
      if (!_.isEmpty(selectedStrip)) {
        const rhythmEventStripDateString = convertToMomentJs(
          selectedStrip.start,
          timezoneOffset,
        ).format('MM/DD');
        let index = _.findIndex(dateList, (x) => x.format('MM/DD') === rhythmEventStripDateString);
        if (index > -1) {
          if (selectedStrip?.startIndex < 0) {
            const diffDay = Math.ceil(Math.abs(selectedStrip.startIndex / 1440));
            index += diffDay;
          }
          setDateIndex(index);
          setSelectedDateValue(dateList[index].toISOString());
          setIsEmptyData(dailyCount[index].count === -1);
        }
      } else if (holterEvents.length === 0 && dateList.length) {
        setDateIndex(0);
        setSelectedDateValue(dateList[0].toISOString());
        setIsEmptyData(dailyCount[0].count === -1);
      }
    }
  }, [isLoadingHrChartOptions, selectedStrip?.id, dateList, holterEvents]);

  useUpdateEffect(() => {
    if (jumpDate) {
      const rhythmEventStripDateString = convertToMomentJs(jumpDate, timezoneOffset).format(
        'MM/DD',
      );
      const index = _.findIndex(dateList, (x) => x.format('MM/DD') === rhythmEventStripDateString);
      if (index > -1) {
        setDateIndex(index);
        setSelectedDateValue(dateList[index].toISOString());
      }
    }
  }, [jumpDate]);

  useEffect(() => {
    const dateListTmp = _.map(dailyCount, (x) => convertToMomentJs(x.date, timezoneOffset));
    setDateList(dateListTmp);
  }, [dailyCount]);

  const formatSelectedDate = dropdownOptions[dateIndex];

  return {
    isLoadingHrChartOptions,
    isLoadingHrChart,
    dateIndex,
    dailyCount,
    isEmptyData,
    isAfibEvent,
    dropdownOptions,
    formatSelectedDate,
    onChangeDate,
    handleClickArrow,
    toggleDisplayRhythmEvent,
  };
};

export default useAfibHrChartOptions;
